import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Globe, Mail, Phone, MapPin, Calendar, Briefcase, Save, Camera, Loader2 } from "lucide-react";
import ProfileSiteEditor from "@/components/ProfileSiteEditor";

const fileUrl = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : null);

const empty = { name: "", website: "", industry: "", registration_no: "", contact_email: "",
  contact_phone: "", address: "", financial_year_start: "April", currency: "INR", overview: "" };

export default function CompanyProfile() {
  const { companyId, companies, company, loadCompanies, selectCompany } = useApp();
  const { hasPermission } = useAuth();
  const [form, setForm] = useState(empty);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const canManage = hasPermission("manage_company");

  const onLogoUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setForm((prev) => ({ ...prev, logo_url: data.url }));
      toast.success("Logo uploaded — remember to save");
    } catch {
      toast.error("Logo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (company && !creating) setForm({ ...empty, ...company });
  }, [company, creating]);

  const save = async () => {
    try {
      if (creating) {
        const { data } = await api.post("/companies", form);
        await loadCompanies();
        selectCompany(data.id);
        setCreating(false);
        toast.success("Company created");
      } else {
        await api.put(`/companies/${companyId}`, form);
        await loadCompanies();
        toast.success("Company updated");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="company-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Company Profile</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage your workspace details</p>
        </div>
        {canManage && (
          <button data-testid="new-company-btn" onClick={() => { setCreating(true); setForm(empty); }}
            className="bg-white border border-slate-200 hover:bg-slate-50 font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 text-slate-700">
            <Plus size={18} /> New Company
          </button>
        )}
      </div>

      {/* Company chips */}
      <div className="flex gap-2 flex-wrap">
        {companies.map((c) => (
          <button key={c.id} data-testid={`company-chip-${c.id}`} onClick={() => { setCreating(false); selectCompany(c.id); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
              c.id === companyId && !creating ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
            }`}>{c.name}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-4 pb-6 border-b border-slate-100">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-indigo-600 grid place-items-center text-white text-2xl font-heading font-extrabold overflow-hidden">
              {form.logo_url ? (
                <img src={fileUrl(form.logo_url)} alt="logo" className="w-full h-full object-cover" data-testid="company-logo-preview" />
              ) : (
                (form.name || "S").slice(0, 1)
              )}
            </div>
            {canManage && (
              <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-slate-200 grid place-items-center cursor-pointer shadow-sm hover:bg-slate-50" title="Upload logo">
                <input type="file" accept="image/*" className="hidden" data-testid="logo-input" onChange={onLogoUpload} />
                {uploading ? <Loader2 size={14} className="animate-spin text-indigo-600" /> : <Camera size={14} className="text-indigo-600" />}
              </label>
            )}
          </div>
          <div>
            <h2 className="text-xl font-heading font-extrabold text-slate-900">{creating ? "New Company" : form.name}</h2>
            <p className="text-slate-400 text-sm">{form.industry || "Set up your company details below"}</p>
          </div>
        </div>

        <fieldset disabled={!canManage} className="grid md:grid-cols-2 gap-4 mt-6">
          <F icon={Building2} label="Company Name"><input data-testid="company-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></F>
          <F icon={Briefcase} label="Industry"><input value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} className={inp} /></F>
          <F icon={Globe} label="Website"><input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className={inp} /></F>
          <F icon={Building2} label="Registration No."><input value={form.registration_no || ""} onChange={(e) => set("registration_no", e.target.value)} className={inp} /></F>
          <F icon={Mail} label="Contact Email"><input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inp} /></F>
          <F icon={Phone} label="Contact Phone"><input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={inp} /></F>
          <F icon={Calendar} label="Financial Year Start">
            <select value={form.financial_year_start} onChange={(e) => set("financial_year_start", e.target.value)} className={inp}>
              {["January", "April", "July", "October"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </F>
          <F icon={Globe} label="Base Currency">
            <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inp}>
              <option value="INR">INR (₹)</option><option value="USD">USD ($)</option>
            </select>
          </F>
          <div className="md:col-span-2">
            <F icon={MapPin} label="Address"><input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={inp} /></F>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Company Overview</label>
            <textarea data-testid="company-overview" value={form.overview || ""} onChange={(e) => set("overview", e.target.value)} rows={3} className={inp} />
          </div>
        </fieldset>

        {canManage && (
          <div className="mt-6 flex justify-end">
            <button data-testid="save-company-btn" onClick={save}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-2.5 rounded-xl flex items-center gap-2 active:scale-95">
              <Save size={18} /> {creating ? "Create Company" : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {!creating && companyId && <ProfileSiteEditor companyId={companyId} canManage={canManage} />}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:bg-slate-50";
function F({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-1"><Icon size={13} />{label}</label>
      {children}
    </div>
  );
}
